import React, { useState, useMemo, useRef } from 'react';
import './TruckGraphic.css';
import {
  itemSpan,
  checkPlacement,
  firstAvailablePosition,
} from '../../utils/deliveryOccupancy';

const TruckGraphic = ({ onDropOrder, onRemoveDelivery, onReorderDelivery, onWigglePosition, wigglePosition, disabled = false, deliveries = [], canRemove = true, showTrailerToggle = true, onHideTrailer = null, trailerVisible = true, onToggleTrailer = null, numberOfPositions = 13 }) => {
  const [dragOverZone, setDragOverZone] = useState(null);
  const [draggingDelivery, setDraggingDelivery] = useState(null);
  const [internalShowTrailer, setInternalShowTrailer] = useState(true);
  const containerRef = useRef(null);
  
  // Use parent-controlled visibility if provided, otherwise use internal state
  // If numberOfPositions is 7, trailer should not be visible (truck only mode)
  const inferredTrailerVisible = numberOfPositions > 7;
  const isTrailerVisible = trailerVisible !== undefined ? trailerVisible : (internalShowTrailer && inferredTrailerVisible);
  const coverage = useRef({}); // Ref to track which positions are covered by delivery spans

  // Build a map of position -> item from the deliveries prop
  // Each item has its own loading position, so we track items not deliveries
  const itemsByPosition = useMemo(() => {
    const map = {};
    deliveries.forEach((delivery) => {
      if (delivery.items && delivery.items.length > 0) {
        delivery.items.forEach((item) => {
          const position = item.loading_position;
          if (position) {
            map[position] = { delivery, item };
          }
        });
      }
    });
    return map;
  }, [deliveries]);

  // Calculate the span (positions occupied) for an item.
  // The maths lives in the shared helper so the graphic, the capacity
  // hint and the sidebar counters all agree.
  const getItemSpan = (item) => itemSpan(item);

  // Backward compatibility - get delivery at position
  const getDeliveryAtPosition = (pos, isTrailer = false) => {
    const info = itemsByPosition[pos];
    if (!info) return null;
    return { delivery: info.delivery, item: info.item, offset: 0 };
  };

  // Check if a position is covered by an item's span (not the start position)
  const isPositionCoveredByItem = (pos, isTrailer = false) => {
    let spanCovered = 0;
    // For each item in itemsByPosition, check if it spans over this position
    Object.values(itemsByPosition).forEach(({ delivery, item }) => {
      const itemPos = item.loading_position;
      const itemIsTrailer = itemPos >= 8;
      if (itemIsTrailer !== isTrailer) return;
      
      const span = getItemSpan(item);
      for (let i = 1; i < span; i++) {
        if (itemPos + i === pos) {
          spanCovered = span;
        }
      }
    });
    return { span: spanCovered };
  };

  const handleDragOver = (e, zone) => {
    e.preventDefault();
    if (!disabled) {
      setDragOverZone(zone);
    }
  };

  const handleDragLeave = () => {
    setDragOverZone(null);
  };

  const handleRemoveDelivery = (deliveryId, position) => {
    if (onRemoveDelivery) {
      // Get the specific item ID at this position
      const itemInfo = itemsByPosition[position];
      const deliveryItemId = itemInfo?.item?.id || null;
      onRemoveDelivery(deliveryId, position, deliveryItemId);
    }
  };

  // Handle drag start for reordering within truck
  const handleReorderDragStart = (e, delivery, currentPosition) => {
    e.stopPropagation();
    
    // Find the specific item at this position
    const itemInfo = itemsByPosition[currentPosition];
    const deliveryItemId = itemInfo?.item?.id;
    
    const dragData = {
      type: 'reorder',
      deliveryId: delivery.id,
      orderId: delivery.customer_order_id,
      customerId: delivery.customer_id,
      fromPosition: currentPosition,
      deliveryItemId: deliveryItemId, // Specific item being moved
      lineId: delivery.items[0]?.order_line?.id,
      productCode: delivery.items[0]?.order_line?.product?.code || 'Unknown',
      customerName: delivery.order?.customer?.fullname || 'Unknown',
    };
    
    setDraggingDelivery(dragData);
    
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Check if there's capacity for an item at a given position.
  // Returns { valid: boolean, message: string }
  //
  // This used to read only `items[0]` of each delivery, so a delivery
  // holding several items (a split order's pieces) was invisible to the
  // check and the planner was told space was free that was taken. The
  // shared helper counts every item, and applies the same rules the API
  // enforces.
  const checkCapacityForItem = (item, position, existingDeliveries) =>
    checkPlacement(
      existingDeliveries,
      position,
      getItemSpan(item),
      numberOfPositions,
    );

  // Find the first available position where an item can fit.
  // Returns { position: number, section: 'truck' | 'trailer' } or
  // { position: null, section: null } if no space.
  //
  // The shared helper counts every item of every delivery, not just the
  // first item of each, and respects the run's own position count.
  const findFirstAvailablePosition = (item, existingDeliveries) =>
    firstAvailablePosition(item, existingDeliveries, numberOfPositions);

  const handleDragEnd = () => {
    setDraggingDelivery(null);
    setDragOverZone(null);
  };

  const handleDrop = (e, position) => {
    e.preventDefault();
    if (disabled) return;

    const orderData = e.dataTransfer.getData('application/json');
    if (!orderData) return;
    
    const dragData = JSON.parse(orderData);
    
    // Handle reordering within truck
    if (dragData.type === 'reorder') {
      // Can't drop on same position
      if (dragData.fromPosition === position) {
        setDragOverZone(null);
        setDraggingDelivery(null);
        return;
      }
      
      // Find the delivery being reordered
      const movingDelivery = deliveries.find(d => d.id === dragData.deliveryId);
      if (!movingDelivery) {
        setDragOverZone(null);
        setDraggingDelivery(null);
        return;
      }

      // Check capacity for the move (exclude the delivery being moved).
      // This used to validate `movingDelivery.items[0]` - the FIRST item of
      // the delivery - rather than the item actually being dragged. Dragging
      // the second item of a split order was checked against the wrong
      // span, so the planner let it land somewhere it did not fit.
      const otherDeliveries = deliveries.filter(d => d.id !== dragData.deliveryId);
      const draggedItem = dragData.deliveryItemId
        ? (movingDelivery.items || []).find(
            (i) => Number(i.id) === Number(dragData.deliveryItemId),
          )
        : movingDelivery.items?.[0];
      const capacityCheck = checkCapacityForItem(
        draggedItem || movingDelivery.items?.[0],
        position,
        otherDeliveries,
      );
      
      if (!capacityCheck.valid) {
        alert(capacityCheck.message);
        setDragOverZone(null);
        setDraggingDelivery(null);
        return;
      }
      
      // Call reorder callback if provided
      if (onReorderDelivery) {
        onReorderDelivery(dragData.deliveryId, position, dragData.deliveryItemId);
      }
      setDragOverZone(null);
      setDraggingDelivery(null);
      return;
    }
    
    // Handle new drop from sidebar
    // Calculate the span for this order item
    const size = dragData.size || 1;
    const qty = dragData.qty || 1;
    const span = size * qty;
    
    // Build a mock item object for capacity check
    const mockItem = {
      order_line: {
        size: dragData.size || 1,
        qty: dragData.qty || 1,
      }
    };

    // Always find the first available position (lowest number) regardless of where dropped
    // This ensures items are packed efficiently from the front
    const firstAvailable = findFirstAvailablePosition(mockItem, deliveries);
    
    if (firstAvailable.position === null) {
      // No space available anywhere
      alert(`Cannot place item: requires ${span} position(s) but there is not enough room on the truck or trailer.`);
      setDragOverZone(null);
      return;
    }

    const targetPosition = firstAvailable.position;

    if (onDropOrder) {
      onDropOrder(dragData, targetPosition);
    }
    
    setDragOverZone(null);
  };

  // Get weight code for a delivery
  const getWeightCode = (delivery) => {
    if (!delivery?.items?.[0]?.order_line?.product?.weight_code) return null;
    return parseInt(delivery.items[0].order_line.product.weight_code);
  };

  // Calculate position class based on weight code
  const getTrailerPositionClass = (pos, delivery) => {
    if (pos < 11) return '';
    const weightCode = getWeightCode(delivery);
    if (weightCode === 3) return 'heavy-trailer';
    if (weightCode === 2) return 'medium-trailer';
    return '';
  };

  const positions = [1, 2, 3, 4, 5, 6, 7];
  const trailerPositions = [8, 9, 10, 11, 12, 13];

  // Check for dangerous load condition: positions 11-13 allocated but 8, 9, 10 available
  const hasDangerousLoad = useMemo(() => {
    const positions8To10Occupied = [8, 9, 10].some(pos => itemsByPosition[pos]);
    const positions11To13Occupied = [11, 12, 13].some(pos => itemsByPosition[pos]);
    return !positions8To10Occupied && positions11To13Occupied;
  }, [itemsByPosition]);

  // Calculate overlay positions based on SVG viewBox and container scaling
  // SVG viewBox is 220x500, container is 330px wide (1.5x scale)
  // Height is multiplied by size * qty from the delivery item
  // REVERSED: Position 1 is at bottom, position 13 is at top
  const getOverlayStyle = (pos, isTrailer = false, delivery = null) => {
    const scale = 1;
    const x = 117 * scale;
    // Reversed: trailer positions (8-13) go from top (y=35) to bottom, truck positions (1-7) go from top to bottom
    const baseY = isTrailer ? 33 : 70;
    const maxIdx = isTrailer ? 5 : 6; // 6 indices for trailer (8-13), 7 for truck (1-7) but idx is 0-based
    const idx = isTrailer ? (pos - 8) : (pos - 1);
    const y = (baseY + idx * 55) * scale;
    
    let height = 48 * scale;
    
    if (delivery?.items?.[0]) {
      const item = delivery.items[0];
      const orderLine = item.order_line;
      const product = orderLine?.product;
      const size = product?.size || 1;
      const qty = item.quantity || 1;
      height = height * size * qty;
    }
    
    return {
      left: `${x}px`,
      top: `${y}px`,
      width: `${210 * scale}px`,
      height: `${height}px`,
    };
  };

  // Separate function for trailer overlay positioning (hitched on top)
  // REVERSED: position 13 is at top (y=35), position 8 is at bottom
  // y = 750 - pos * 55 for spanned items
  const getOverlayStyleTrailer = (pos, idx, delivery = null) => {
    const scale = 1;
    const x = 118 * scale; // Moved right
    
    let span = 1;
    if (delivery?.items?.[0]) {
      const item = delivery.items[0];
      const orderLine = item.order_line;
      const product = orderLine?.product;
      const size = product?.size || 1;
      const qty = item.quantity || 1;
      span = size * qty;
    }
    
    // Calculate y based on pos: position 13 at top (y=35), position 8 at bottom (y=310)
    // Same formula as drop zone rects: 750 - pos * 55
    // For spanned items, offset top upward

    //55px made up of 48px + 7px gap
    //2px dashed border

    const y = 708 - pos * 52; // 2px up

    let height = 47 * scale;
    if (delivery?.items?.[0]) {
      const item = delivery.items[0];
      const orderLine = item.order_line;
      const product = orderLine?.product;
      const size = product?.size || 1;
      const qty = item.quantity || 1;
      height = height * size * qty + (span - 1) ; // Gap reduced to 2
    }
    
    return {
      left: `${x}px`,
      top: `${y}px`,
      width: `${210 * scale}px`,
      height: `${height}px`,
    };
  };

  // Get truck overlay style (hitched below trailer)
  // REVERSED: position 1 is at bottom, position 7 is at top
  // y = 455 - pos * 55 - (span - 1) * 55 for spanned items
  const getOverlayStyleTruck = (pos, idx, delivery = null) => {
    const scale = 1;
    const x = 115 * scale;
    
    let span = 1;
    if (delivery?.items?.[0]) {
      const item = delivery.items[0];
      const orderLine = item.order_line;
      const product = orderLine?.product;
      const size = product?.size || 1;
      const qty = item.quantity || 1;
      span = size * qty;
    }
    
    // Calculate y based on pos: position 1 at bottom (y=400), position 7 at top (y=70)
    // For spanned items, offset top upward
    const y = (405 - pos * 55 - (span - 1) * 55) * scale;
    
    let height = 48 * scale;
    if (delivery?.items?.[0]) {
      const item = delivery.items[0];
      const orderLine = item.order_line;
      const product = orderLine?.product;
      const size = product?.size || 1;
      const qty = item.quantity || 1;
      height = height * size * qty;
    }
    
    return {
      left: `${x}px`,
      top: `${y}px`,
      width: `${220 * scale}px`,
      height: `${height}px`,
    };
  };

  return (
    <div className="truck-graphic-container" ref={containerRef}>
      <div className="trailer-toggle">
        {showTrailerToggle && (
          <button 
            className={`trailer-toggle-btn ${isTrailerVisible ? 'active' : ''}`}
            onClick={() => {
              if (isTrailerVisible && onHideTrailer) {
                // User wants to hide trailer - call callback to handle confirmation and API
                onHideTrailer();
              } else if (onToggleTrailer) {
                onToggleTrailer(!isTrailerVisible);
              }
            }}
            title={isTrailerVisible ? 'Hide trailer' : 'Show trailer'}
          >
            {isTrailerVisible ? '🚛 Truck & Trailer' : '🚛 Truck Only'}
          </button>
        )}
        {hasDangerousLoad && (
          <div className="dangerous-load-warning">
            ⚠️ Dangerous load: Rear-heavy trailer - fill positions 8-10 first
          </div>
        )}
      </div>

      {/* Labels on the left */}
      <div className="labels-wrapper">
        {isTrailerVisible && <div className="vehicle-label trailer-label">Trailer</div>}
        <div className="vehicle-label truck-label">Truck</div>
      </div>

      {/* Truck and Trailer graphics, vertically stacked and hitched */}
      <div className="vehicles-wrapper">
        {/* Trailer - positioned at top (hitched to back of truck) */}
        {isTrailerVisible ? (
          <div className="vehicle-section trailer-section">
            <div className="truck-svg-wrapper trailer-svg-wrapper">
              <svg viewBox="0 0 220 400" className="truck-svg trailer-svg">
            {/* Trailer Hitch - connects to truck at bottom */}
            <rect 
              x="150" 
              y="375" 
              width="20" 
              height="25" 
              className="trailer-hitch"
            />
            
            {/* Trailer Body - cargo area */}
            <rect 
              x="40" 
              y="25" 
              width="240" 
              height="350" 
              className="trailer-body"
            />
            
            {/* Side walls */}
            <rect x="35" y="25" width="5" height="350" className="truck-wall" />
            <rect x="280" y="25" width="5" height="350" className="truck-wall" />
            
            {/* Drop Zone outlines - for visual reference - REVERSED: pos 13 at top, pos 8 at bottom */}
            {[...trailerPositions].reverse().map((pos, idx) => {
              const x = 50;
              // Calculate y based on pos: position 13 at top (y=35), position 8 at bottom (y=305)
              const y = 750 - pos * 55;
              const itemInfo = itemsByPosition[pos];
              const isOccupied = !!itemInfo;
              const isDragOver = dragOverZone === pos;
              
              const weightCode = itemInfo?.item?.order_line?.product?.weight_code;
              const isHeavyTrailer = pos >= 8 && pos <= 10 && weightCode === 3;
              const isMediumTrailer = pos >= 8 && pos <= 10 && weightCode === 2;
              
              let bgClass = 'drop-zone-bg';
              if (isOccupied) {
                bgClass += ' occupied';
                if (isHeavyTrailer) bgClass += ' heavy-trailer';
                if (isMediumTrailer) bgClass += ' medium-trailer';
              }
              if (isDragOver) bgClass += ' drag-over';
              
              return (
                <rect
                  key={pos}
                  x={x}
                  y={y}
                  width="220"
                  height="48"
                  className={bgClass}
                />
              );
            })}
          </svg>
          
          {/* Draggable overlays for occupied zones - spans multiple positions based on size * qty - REVERSED: pos 13 at top */}
          {[...trailerPositions].reverse().map((pos, idx) => {
            // Only show overlay at the start position of each item
            const deliveryInfo = getDeliveryAtPosition(pos, true);
            if (!deliveryInfo || deliveryInfo.offset !== 0) return null;
            
            const delivery = deliveryInfo.delivery;
            const item = deliveryInfo.item;
            const span = getItemSpan(item);
            const isDragOver = dragOverZone === pos;
            const isDragging = draggingDelivery?.deliveryId === delivery.id;
            const positionClass = getTrailerPositionClass(pos, delivery);
            
            // Calculate y based on pos: position 13 at top (y=35), position 8 at bottom (y=305)
            // For spanned items, offset top upward so item appears at correct visual position
            // y = 750 - pos * 55 - (span - 1) * 55
            const y = 750 - pos * 55 - (span - 1) * 55 - 6; // 2px up
            const height = span * 49 + (span - 1) * 2; // Gap reduced to 2
            
            return (
              <div
                key={`overlay-${pos}`}
                className={`drop-zone-overlay occupied ${positionClass} ${isDragOver ? 'drag-over' : ''} ${isDragging ? 'dragging' : ''} ${wigglePosition === pos ? 'wiggle' : ''}`}
                style={{
                  left: '117px',
                  top: `${y}px`,
                  width: '210px',
                  height: `${height}px`,
                }}
                draggable
                onDragStart={(e) => handleReorderDragStart(e, delivery, pos)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, pos)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, pos)}
                onClick={() => onWigglePosition && onWigglePosition(pos)}
              >
                <div className="overlay-content">
                  <div className="overlay-line">
                    <span className="position-label">{delivery.order?.order_external_reference || 'ORDER #'}</span>
                    <span className="customer-label">
                      {delivery.order?.customer?.fullname || 'Unknown'}
                    </span>
                  </div>
                  <div className="overlay-line">
                    <span className="product-label">
                      {item?.order_line?.product?.code || 'Product'}
                    </span>
                    <span className="span-label">{span} cube</span>
                  </div>
                </div>
                {canRemove && (
                  <button 
                    className="remove-btn-overlay"
                    onClick={() => handleRemoveDelivery(delivery.id, pos)}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
          
          {/* Drop zones for empty trailer positions - skip positions covered by spans */}
          {[...trailerPositions].reverse().map((pos, idx) => {
            const deliveryInfo = getDeliveryAtPosition(pos, true);
            const dropzoneCovered = isPositionCoveredByItem(pos, true);

            // Skip if this position is covered by a delivery's span (not the start position)
            if (deliveryInfo || dropzoneCovered.span >= 1) return null;
            
            const isDragOver = dragOverZone === pos;
            
            return (
              <div
                key={`overlay-${pos}`}
                className={`drop-zone-overlay empty ${isDragOver ? 'drag-over' : ''}`}
                style={getOverlayStyleTrailer(pos, idx)}
                onDragOver={(e) => handleDragOver(e, pos)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, pos)}
              >
                <span className="empty-label">{pos}</span>
              </div>
            );
          })}
          </div>
        </div>
        ) : null}

        {/* Truck - positioned at bottom (cab at front/bottom) */}
        <div className="vehicle-section truck-section">
          <div className="truck-svg-wrapper">
          <svg viewBox="0 0 220 550" className="truck-svg">
            {/* Truck Cab - front of truck (bottom) */}
            <rect 
              x="50" 
              y="400" 
              width="220" 
              height="70" 
              className="truck-cab"
            />
            
            {/* Truck Body - main cargo area in front of cab */}
            <rect 
              x="40" 
              y="0" 
              width="240" 
              height="400" 
              className="truck-body"
            />
            
            {/* Side walls */}
            <rect x="35" y="0" width="5" height="400" className="truck-wall" />
            <rect x="280" y="0" width="5" height="400" className="truck-wall" />
            
            {/* Drop Zone outlines - for visual reference - REVERSED: pos 1 at bottom, pos 7 at top */}
            {[...positions].reverse().map((pos, idx) => {
              const x = 50;
              // Calculate y based on pos: position 1 at bottom (y=400), position 7 at top (y=70)
              const y = 405 - pos * 55;
              const itemInfo = itemsByPosition[pos];
              const isOccupied = !!itemInfo;
              const isDragOver = dragOverZone === pos;
              
              let bgClass = 'drop-zone-bg';
              if (isOccupied) bgClass += ' occupied';
              if (isDragOver) bgClass += ' drag-over';
              
              return (
                <rect
                  key={pos}
                  x={x}
                  y={y}
                  width="220"
                  height="48"
                  className={bgClass}
                />
              );
            })}
          </svg>
          
          {/* Draggable overlays for occupied zones - REVERSED: pos 1 at bottom, pos 7 at top */}
          {[...positions].reverse().map((pos, idx) => {
            const deliveryInfo = getDeliveryAtPosition(pos, false);
            if (!deliveryInfo) return null;
            
            const delivery = deliveryInfo.delivery;
            const item = deliveryInfo.item;
            const span = getItemSpan(item);
            const isDragOver = dragOverZone === pos;
            const isDragging = draggingDelivery?.deliveryId === delivery.id;
            
            // Calculate y based on pos: position 1 at bottom (y=400), position 7 at top (y=70)
            // For spanned items, offset top upward so item appears at correct visual position
            // y = 455 - pos * 55 - (span - 1) * 55
            const y = 405 - pos * 55 - (span - 1) * 55;
            const height = span * 48 + (span - 1) * 7;

            console.log(item);
            
            return (
              <div
                key={`overlay-${pos}`}
                className={`drop-zone-overlay occupied ${isDragOver ? 'drag-over' : ''} ${isDragging ? 'dragging' : ''} ${wigglePosition === pos ? 'wiggle' : ''}`}
                style={{
                  left: '115px',
                  top: `${y}px`,
                  width: '220px',
                  height: `${height}px`,
                }}
                draggable
                onDragStart={(e) => handleReorderDragStart(e, delivery, pos)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, pos)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, pos)}
                onClick={() => onWigglePosition && onWigglePosition(pos)}
              >
                <div className="overlay-content">
                  <div className="overlay-line">
                    <span className="position-label">{delivery.order?.order_external_reference || 'ORDER #'}</span>
                    <span className="customer-label">
                      {delivery.order?.customer?.fullname || 'Unknown'}
                    </span>
                  </div>
                  <div className="overlay-line">
                    <span className="product-label">
                      {item?.order_line?.product?.code || 'Product'}
                    </span>
                    <span className="span-label">{span} cube</span>
                  </div>
                </div>
                {canRemove && (
                  <button 
                    className="remove-btn-overlay"
                    onClick={() => handleRemoveDelivery(delivery.id, pos)}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
          
          {/* Drop zones for empty truck positions - skip positions covered by spans - REVERSED */}
          {[...positions].reverse().map((pos, idx) => {
            const deliveryInfo = getDeliveryAtPosition(pos, false);
            const dropzoneCovered = isPositionCoveredByItem(pos, false);

            // Skip if this position is covered by a delivery's span (not the start position)
            if (deliveryInfo || dropzoneCovered.span >= 1) return null;
            
            const isDragOver = dragOverZone === pos;
            return (
              <div
                key={`overlay-${pos}`}
                className={`drop-zone-overlay empty ${isDragOver ? 'drag-over' : ''}`}
                style={getOverlayStyleTruck(pos, idx)}
                onDragOver={(e) => handleDragOver(e, pos)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, pos)}
              >
                <span className="empty-label">{pos}</span>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TruckGraphic;
